import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, ShieldCheck, PhoneCall, FileCheck2, Zap, Receipt,
  BadgeDollarSign, CalendarCheck2, Bot, RefreshCcw, Check, ArrowUp
} from 'lucide-react';

const aiProducts = [
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Patient Billing",
  "AI Denial Management",
  "AI EOB Posting",
  "AI AR & Collections",
  "AI Insurance Calling",
  "AI Payment Reconciliation",
  "AI Receptionist",
  "AI Hygiene Recall",
  "AI Case Acceptance",
  "AI Claim Scrubbing",
  "AI Schedule Optimizer",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Patient Experience",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
];

const scenarios = [
  {
    prompt: "Verify insurance for tomorrow's patients",
    agents: [
      { icon: ShieldCheck, color: '#38bdf8', name: 'Insurance Verification Agent', working: 'Checking 23 payer portals…', done: '23 patients verified' },
      { icon: PhoneCall, color: '#a78bfa', name: 'Insurance Calling Agent', working: 'On the phone with Delta Dental…', done: '4 full breakdowns collected' },
      { icon: FileCheck2, color: '#34d399', name: 'Patient Intake Agent', working: 'Syncing coverage to charts…', done: 'All charts updated' },
    ],
  },
  {
    prompt: 'Collect everything insurance owes us',
    agents: [
      { icon: Receipt, color: '#38bdf8', name: 'Claim Scrubbing Agent', working: 'Scrubbing 41 claims…', done: '41 claims, 0 errors' },
      { icon: Zap, color: '#fbbf24', name: 'Claim Submission Agent', working: 'Submitting batch to payers…', done: '41 claims submitted' },
      { icon: BadgeDollarSign, color: '#34d399', name: 'AR & Collections Agent', working: 'Working the aging report…', done: '$18,240 recovered' },
    ],
  },
  {
    prompt: 'Fill every open slot this week',
    agents: [
      { icon: CalendarCheck2, color: '#a78bfa', name: 'Hygiene Recall Agent', working: 'Texting 67 overdue patients…', done: '12 recalls booked' },
      { icon: Bot, color: '#38bdf8', name: 'AI Receptionist', working: 'Answering inbound calls…', done: '9 appointments scheduled' },
      { icon: RefreshCcw, color: '#34d399', name: 'Schedule Optimizer', working: 'Rebalancing the columns…', done: '94% of chairs filled' },
    ],
  },
];

const AgentCard = ({ agent, index }) => {
  const [status, setStatus] = useState('working');
  const Icon = agent.icon;

  useEffect(() => {
    const t = setTimeout(() => setStatus('done'), 1500 + index * 550);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, delay: index * 0.45, ease: 'easeOut' }}
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-3.5 py-3"
    >
      <div className="relative shrink-0">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10"
          style={{ background: `${agent.color}1f` }}
        >
          <Icon className="w-4.5 h-4.5" style={{ color: agent.color, width: 18, height: 18 }} />
        </div>
        {status === 'working' && (
          <motion.div
            className="absolute -inset-1 rounded-xl border"
            style={{ borderColor: agent.color }}
            animate={{ opacity: [0.6, 0, 0.6], scale: [1, 1.15, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-white leading-tight truncate">{agent.name}</div>
        <AnimatePresence mode="wait">
          {status === 'working' ? (
            <motion.div
              key="working"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[12px] text-slate-400 truncate"
            >
              {agent.working}
            </motion.div>
          ) : (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[12px] font-medium text-emerald-400 truncate"
            >
              {agent.done}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="shrink-0">
        {status === 'working' ? (
          <motion.div
            className="w-4 h-4 rounded-full border-2 border-slate-600 border-t-slate-300"
            animate={{ rotate: 360 }}
            transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
          />
        ) : (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
            className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"
          >
            <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const MobileAdV6HeroSection = () => {
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('typing'); // typing | spawning | done
  const scenario = scenarios[scenarioIdx];

  // Type the prompt character by character
  useEffect(() => {
    if (phase !== 'typing') return;
    if (typed.length < scenario.prompt.length) {
      const t = setTimeout(() => setTyped(scenario.prompt.slice(0, typed.length + 1)), 36);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase('spawning'), 550);
    return () => clearTimeout(t);
  }, [phase, typed, scenario.prompt]);

  // Wait for all agents to spawn and finish, then show the summary
  useEffect(() => {
    if (phase !== 'spawning') return;
    const total = 1500 + scenario.agents.length * 550 + 600;
    const t = setTimeout(() => setPhase('done'), total);
    return () => clearTimeout(t);
  }, [phase, scenario.agents.length]);

  // Hold the summary, then loop to the next scenario
  useEffect(() => {
    if (phase !== 'done') return;
    const t = setTimeout(() => {
      setTyped('');
      setPhase('typing');
      setScenarioIdx((i) => (i + 1) % scenarios.length);
    }, 2800);
    return () => clearTimeout(t);
  }, [phase]);

  const marquee = [...aiProducts, ...aiProducts];

  return (
    <section className="relative min-h-screen overflow-hidden flex flex-col" style={{ background: '#030712' }}>
      {/* Background: grid + aurora glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.13]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.4) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 35%, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 35%, black 30%, transparent 75%)',
          }}
        />
        <motion.div
          className="absolute top-[-15%] left-[-15%] w-[70%] h-[55%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(56,130,246,0.35) 0%, transparent 70%)', filter: 'blur(70px)' }}
          animate={{ x: [0, 40, 0], y: [0, 25, 0], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-[15%] right-[-20%] w-[65%] h-[50%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)', filter: 'blur(70px)' }}
          animate={{ x: [0, -35, 0], y: [0, 30, 0], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 17, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />
        <motion.div
          className="absolute bottom-[-10%] left-[20%] w-[55%] h-[40%] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.18) 0%, transparent 70%)', filter: 'blur(70px)' }}
          animate={{ x: [0, 25, 0], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
        />
      </div>

      <div className="relative z-10 w-full max-w-xl mx-auto px-5 pt-12 pb-4 flex-1 flex flex-col">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-7">
          <img src="/assets/alogo.png" alt="Asha Dental" className="h-8 w-auto rounded-lg shadow-sm" />
          <span className="font-bold text-xl tracking-wide text-white">Asha Dental</span>
        </div>

        {/* Headline */}
        <div className="text-center mb-3">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[2.6rem] sm:text-6xl font-black leading-[1.04] tracking-[-0.03em] text-white"
          >
            The first{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-400 to-violet-400">
              AI-native
            </span>
            <br />
            dental PMS
          </motion.h1>
        </div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-center text-base sm:text-lg text-slate-400 font-medium mb-8 max-w-md mx-auto leading-snug"
        >
          One prompt spawns AI agents that run your entire office.{' '}
          <span className="text-slate-200 font-semibold">20 AI products. 1 platform.</span>
        </motion.p>

        {/* The OS home screen */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="relative mx-auto w-full max-w-md"
        >
          {/* Glow behind the panel */}
          <div className="absolute -inset-1.5 rounded-[26px] bg-gradient-to-r from-sky-500/40 via-blue-500/30 to-violet-500/40 blur-xl opacity-70" />

          <div className="relative rounded-3xl border border-white/10 bg-slate-950/80 backdrop-blur-xl overflow-hidden shadow-2xl">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[13px] font-bold text-white tracking-wide">Asha OS</span>
              </div>
              <div className="flex items-center gap-1.5">
                <motion.div
                  className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-[11px] text-slate-400 font-medium">20 agents online</span>
              </div>
            </div>

            {/* Prompt bar */}
            <div className="px-4 pt-4">
              <div className="relative rounded-2xl border border-white/15 bg-white/[0.05] px-4 py-3.5 flex items-center gap-3">
                <div className="flex-1 text-[15px] text-slate-100 font-medium leading-snug min-h-[22px]">
                  {typed}
                  <motion.span
                    className="inline-block w-[2px] h-[18px] ml-0.5 align-middle bg-sky-400 rounded-full"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.9, repeat: Infinity }}
                  />
                </div>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                    phase === 'typing' ? 'bg-white/10' : 'bg-gradient-to-br from-sky-400 to-violet-500'
                  }`}
                >
                  <ArrowUp className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Spawned agents */}
            <div className="px-4 py-4 min-h-[228px]">
              <AnimatePresence mode="wait">
                {phase !== 'typing' && (
                  <motion.div key={scenarioIdx} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold"
                    >
                      <Zap className="w-3 h-3 text-sky-400" />
                      Spawning agents
                    </motion.div>
                    <div className="space-y-2.5">
                      {scenario.agents.map((agent, i) => (
                        <AgentCard key={agent.name} agent={agent} index={i} />
                      ))}
                    </div>
                    <AnimatePresence>
                      {phase === 'done' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2.5"
                        >
                          <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                          </div>
                          <span className="text-[13px] font-semibold text-emerald-300">
                            Done. Your office is running itself.
                          </span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Marquee of all 20 AI products */}
      <div className="relative z-10 pb-10 pt-4">
        <div
          className="overflow-hidden"
          style={{
            maskImage: 'linear-gradient(90deg, transparent, black 12%, black 88%, transparent)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent, black 12%, black 88%, transparent)',
          }}
        >
          <motion.div
            className="flex gap-2.5 w-max"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
          >
            {marquee.map((product, i) => (
              <div
                key={i}
                className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[13px] font-medium text-slate-300 whitespace-nowrap"
              >
                {product}
              </div>
            ))}
          </motion.div>
        </div>
        <div className="text-center mt-4 text-[12px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
          20 AI products · 1 platform · asha.health
        </div>
      </div>
    </section>
  );
};

export default MobileAdV6HeroSection;
